import { Html5Qrcode } from "html5-qrcode";
import { useEffect, useRef, useState, useCallback } from "react";
import { ScanAPI } from "../../services/scan.api";
import { AuthStore } from "../../auth/authStore";
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCcw,
  LogOut,
  ScanLine,
} from "lucide-react";

import {
  playSuccessSound,
  playWarningSound,
  playErrorSound,
} from "../../utils/scanSound";

const SCAN_THROTTLE_MS = 2000;
const AUTO_RESET_SUCCESS_MS = 3000;
const AUTO_RESET_OTHER_MS = 4000;

export default function ScanPage() {
  const scannerRef = useRef(null);
  const lastScanRef = useRef(0);
  const isStartingRef = useRef(false);

  const [status, setStatus] = useState("idle");
  const [result, setResult] = useState(null);

  /* ==============================
     LIFECYCLE
  ============================== */

  useEffect(() => {
    startScanner();
    return () => stopScanner();
  }, []);

  /* ==============================
     SCANNER CONTROL
  ============================== */

  const startScanner = useCallback(async () => {
    if (scannerRef.current || isStartingRef.current) return;

    const element = document.getElementById("qr-reader");
    if (!element) return;

    isStartingRef.current = true;

    const qr = new Html5Qrcode("qr-reader");
    scannerRef.current = qr;

    try {
      await qr.start(
        { facingMode: "environment" },
        { fps: 8, qrbox: { width: 240, height: 240 } },
        handleScan
      );
    } catch (err) {
      console.error("Scanner start error:", err);
      transition("error");
    } finally {
      isStartingRef.current = false;
    }
  }, []);

  const stopScanner = useCallback(async () => {
    if (!scannerRef.current) return;

    try {
      await scannerRef.current.stop();
      await scannerRef.current.clear();
    } catch {}

    scannerRef.current = null;
  }, []);

  /* ==============================
     CORE LOGIC
  ============================== */

  function canScan() {
    const now = Date.now();
    if (now - lastScanRef.current < SCAN_THROTTLE_MS) return false;
    lastScanRef.current = now;
    return true;
  }

  async function handleScan(text) {
    if (!canScan()) return;

    await stopScanner();
    transition("loading");

    try {
      let parsed;

      try {
        parsed = JSON.parse(text);
      } catch {
        setResult({ reason: "QR format invalid" });
        transition("invalid");
        return;
      }

      const { bookingId } = parsed;

      if (!bookingId) {
        setResult({ reason: "QR tidak lengkap" });
        transition("invalid");
        return;
      }

      const preview = await ScanAPI.check(bookingId);
      const previewData = preview.data;

      if (previewData.status === "invalid") {
        setResult(previewData);
        transition("invalid");
        return;
      }

      if (previewData.status === "unpaid") {
        setResult({ reason: "Pembayaran belum dikonfirmasi" });
        transition("invalid");
        return;
      }

      if (previewData.status === "used") {
        setResult(previewData);
        transition("used");
        return;
      }

      const execute = await ScanAPI.execute(bookingId, {
        qrPayload: text,
      });

      const data = execute.data;
      setResult(data);

      if (!data.valid) {
        if (data.reason === "Sudah digunakan") {
          transition("used");
        } else {
          transition("invalid");
        }
        return;
      }

      transition("success");
    } catch (err) {
      console.error("Scan error:", err);
      transition("error");
    }
  }

  /* ==============================
     STATE + SOUND
  ============================== */

  function transition(next) {
    setStatus(next);
    playSound(next);
  }

  function playSound(state) {
    switch (state) {
      case "success":
        playSuccessSound();
        break;
      case "used":
        playWarningSound();
        break;
      case "invalid":
      case "error":
        playErrorSound();
        break;
      default:
        break;
    }
  }

  async function resetScan() {
    setStatus("idle");
    setResult(null);
    lastScanRef.current = 0;
    await stopScanner();
    setTimeout(startScanner, 300);
  }

  function handleLogout() {
    AuthStore.logout({ redirect: false });
    window.location.replace("/admin");
  }

  /* ==============================
     RENDER
  ============================== */

  return (
    <div className="sp">
      {/* HEADER */}
      <header className="sp__header">
        <div className="sp__header-brand">
          <ScanLine size={20} />
          <span>Scan Tiket</span>
        </div>
        <button className="sp__logout" onClick={handleLogout} title="Keluar">
          <LogOut size={18} />
        </button>
      </header>

      {/* SCANNER */}
      {status === "idle" && (
        <div className="sp__body">
          <div className="sp__scanner-card">
            <div className="sp__viewfinder">
              <div id="qr-reader" />
              <div className="sp__corners" aria-hidden="true">
                <span /><span /><span /><span />
              </div>
            </div>
            <p className="sp__hint">Arahkan kamera ke QR Code tiket</p>
          </div>
        </div>
      )}

      {/* RESULT */}
      {status !== "idle" && (
        <ScanResult
          status={status}
          result={result}
          onReset={resetScan}
        />
      )}
    </div>
  );
}

/* ======================================
   RESULT COMPONENT
====================================== */

function ScanResult({ status, result, onReset }) {
  const autoMs =
    status === "success" ? AUTO_RESET_SUCCESS_MS : AUTO_RESET_OTHER_MS;

  const [remaining, setRemaining] = useState(autoMs);

  useEffect(() => {
    if (status === "loading") return;

    setRemaining(autoMs);

    const interval = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 100) {
          clearInterval(interval);
          onReset();
          return 0;
        }
        return prev - 100;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [status]);

  const progress = Math.max(0, (remaining / autoMs) * 100);

  const config = {
    loading: {
      title: "Memeriksa...",
      icon: null,
      mod: "loading",
    },
    success: {
      title: "Tiket Valid",
      icon: <CheckCircle size={72} strokeWidth={1.5} />,
      mod: "success",
    },
    used: {
      title: "Sudah Digunakan",
      icon: <AlertTriangle size={72} strokeWidth={1.5} />,
      mod: "used",
    },
    invalid: {
      title: result?.reason || "QR Tidak Valid",
      icon: <XCircle size={72} strokeWidth={1.5} />,
      mod: "invalid",
    },
    error: {
      title: "Terjadi Kesalahan",
      icon: <XCircle size={72} strokeWidth={1.5} />,
      mod: "error",
    },
  }[status];

  return (
    <div className={`sp__result sp__result--${config.mod}`}>
      {status === "loading" ? (
        <div className="sp__result-loading">
          <div className="sp__spinner" />
          <p>Memeriksa tiket...</p>
        </div>
      ) : (
        <>
          <div className="sp__result-icon">{config.icon}</div>
          <h2 className="sp__result-title">{config.title}</h2>

          {status === "success" && (
            <div className="sp__result-info">
              {result?.name && (
                <p className="sp__result-name">{result.name}</p>
              )}
              {result?.ticketType && (
                <span className="sp__result-tier">{result.ticketType}</span>
              )}
              {result?.event && (
                <p className="sp__result-event">{result.event}</p>
              )}
              {result?.ticketCode && (
                <p className="sp__result-code">{result.ticketCode}</p>
              )}
            </div>
          )}

          {status === "used" && (
            <div className="sp__result-info">
              <div className="sp__used-list">
                {result?.tickets?.length > 0
                  ? result.tickets.map((t, i) => (
                      <div key={i} className="sp__used-item">
                        <span className="sp__used-code">{t.code}</span>
                        <span className="sp__used-time">
                          {t.scannedAt
                            ? new Date(t.scannedAt).toLocaleString("id-ID")
                            : "—"}
                        </span>
                      </div>
                    ))
                  : result?.scannedAt && (
                      <div className="sp__used-item">
                        <span className="sp__used-code">{result?.ticketCode}</span>
                        <span className="sp__used-time">
                          {new Date(result.scannedAt).toLocaleString("id-ID")}
                        </span>
                      </div>
                    )}
              </div>
            </div>
          )}

          {/* COUNTDOWN BAR */}
          <div className="sp__countdown">
            <div
              className="sp__countdown-bar"
              style={{ width: `${progress}%` }}
            />
          </div>

          <button className="sp__reset" onClick={onReset}>
            <RefreshCcw size={16} />
            Scan Lagi
          </button>
        </>
      )}
    </div>
  );
}
